import { Booking, Resource } from '@/types';
import {
  MatchSignal,
  ResourceMatchResult,
  RecommendationEngineInput,
  RecommendationEngineOutput,
  AvailablePeriod,
  ConflictDetail,
} from '@/types/recommendation';

// ---------------------------------------------------------------------------
// Role synonym / family map
// Maps canonical role names to a set of equivalent or closely related titles.
// All matching is case-insensitive. This data lives entirely in this product —
// no external dependency required.
// ---------------------------------------------------------------------------
const ROLE_FAMILIES: Record<string, string[]> = {
  'frontend developer':    ['frontend developer', 'front-end developer', 'front end developer', 'ui developer', 'web developer', 'react developer', 'angular developer', 'vue developer', 'javascript developer'],
  'backend developer':     ['backend developer', 'back-end developer', 'back end developer', 'server-side developer', 'api developer', 'python developer', 'java developer', 'node developer', '.net developer'],
  'fullstack developer':   ['fullstack developer', 'full-stack developer', 'full stack developer', 'software engineer', 'software developer'],
  'senior developer':      ['senior developer', 'senior software engineer', 'lead developer', 'principal developer', 'staff engineer'],
  'ui/ux designer':        ['ui/ux designer', 'ux designer', 'ui designer', 'product designer', 'interaction designer', 'user experience designer', 'user interface designer'],
  'project manager':       ['project manager', 'programme manager', 'program manager', 'delivery manager', 'engagement manager', 'scrum master'],
  'business analyst':      ['business analyst', 'ba', 'systems analyst', 'product analyst', 'requirements analyst'],
  'qa engineer':           ['qa engineer', 'quality assurance engineer', 'test engineer', 'software tester', 'automation engineer', 'sdet'],
  'devops engineer':       ['devops engineer', 'site reliability engineer', 'sre', 'platform engineer', 'infrastructure engineer', 'cloud engineer'],
  'data analyst':          ['data analyst', 'data engineer', 'bi analyst', 'business intelligence analyst', 'reporting analyst'],
  'solution architect':    ['solution architect', 'software architect', 'enterprise architect', 'technical architect', 'systems architect'],
};

/** Build a reverse lookup: any alias → canonical family name */
const ALIAS_TO_FAMILY: Map<string, string> = new Map();
for (const [canonical, aliases] of Object.entries(ROLE_FAMILIES)) {
  for (const alias of aliases) {
    ALIAS_TO_FAMILY.set(alias.toLowerCase(), canonical);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isBookingForResource(booking: Booking, resourceId: string): boolean {
  if (booking.resourceId && String(booking.resourceId) === resourceId) return true;
  if (booking.resourceIds && booking.resourceIds.includes(resourceId)) return true;
  return false;
}

function dateOnlyMs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Calendar days between two dates (inclusive of both endpoints) */
function calendarDaysBetween(a: Date, b: Date): number {
  return Math.max(1, Math.round(Math.abs(dateOnlyMs(b) - dateOnlyMs(a)) / 86400000) + 1);
}

// ---------------------------------------------------------------------------
// Hard filter: Availability
// Uses hour-level granularity so that partial-day bookings (e.g. 2 hours)
// don't mark the entire day as "fully booked".
// ---------------------------------------------------------------------------
const WORKING_HOURS_PER_DAY = 8;

interface AvailabilityResult {
  isFullyBooked: boolean;
  availablePeriods: AvailablePeriod[];
  conflictDetails: ConflictDetail | null;
  explanation: string;
}

function computeAvailabilitySignal(
  resource: Resource,
  startDate: Date,
  endDate: Date,
  existingBookings: Booking[]
): AvailabilityResult {
  // endDate is an inclusive calendar date (midnight of that day).
  // For overlap detection, treat it as exclusive by advancing to next day midnight.
  const exclusiveEnd = new Date(endDate);
  exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);

  const resourceBookings = existingBookings.filter(b =>
    isBookingForResource(b, resource.id) &&
    new Date(b.startTime) < exclusiveEnd &&
    new Date(b.endTime) > startDate
  );

  const startMs = dateOnlyMs(startDate);
  const endMs = dateOnlyMs(endDate);
  const totalDays = Math.max(1, Math.round((endMs - startMs) / 86400000) + 1);
  const totalHours = totalDays * WORKING_HOURS_PER_DAY;

  const periodStartMs = startDate.getTime();
  const periodEndMs = exclusiveEnd.getTime();
  let bookedHours = 0;
  for (const booking of resourceBookings) {
    const bStartMs = new Date(booking.startTime).getTime();
    const bEndMs = new Date(booking.endTime).getTime();
    const overlapStart = Math.max(bStartMs, periodStartMs);
    const overlapEnd = Math.min(bEndMs, periodEndMs);
    if (overlapEnd > overlapStart) {
      bookedHours += (overlapEnd - overlapStart) / 3600000;
    }
  }
  bookedHours = Math.min(bookedHours, totalHours);

  const freeHours = totalHours - bookedHours;
  const isFullyBooked = freeHours <= 0;

  let explanation: string;
  if (isFullyBooked) {
    explanation = 'Fully booked for this period';
  } else if (bookedHours === 0) {
    explanation = 'Free for the full booking period';
  } else {
    const freeRounded = Math.round(freeHours * 10) / 10;
    const totalRounded = Math.round(totalHours * 10) / 10;
    explanation = `Partially free — ${freeRounded}h of ${totalRounded}h available`;
  }

  const availablePeriods: AvailablePeriod[] = [];
  if (!isFullyBooked) {
    availablePeriods.push({
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      type: bookedHours === 0 ? 'full' : 'partial',
    });
  }

  let conflictDetails: ConflictDetail | null = null;
  if (isFullyBooked && resourceBookings.length > 0) {
    const conflictBooking = resourceBookings[0];
    const conflictingResourceCount = conflictBooking.resourceIds
      ? conflictBooking.resourceIds.length
      : 1;
    const impactSummary =
      conflictingResourceCount === 1
        ? `Project ${conflictBooking.projectName || 'Unknown'} will be left with no resource`
        : `Project ${conflictBooking.projectName || 'Unknown'} will go from ${conflictingResourceCount} to ${conflictingResourceCount - 1} resources for this period`;

    conflictDetails = {
      conflictingBookingId: String(conflictBooking.id),
      conflictingProjectId: String(conflictBooking.projectId || ''),
      conflictingProjectName: conflictBooking.projectName || 'Unknown',
      allocationPercent: 100,
      overlapStart: conflictBooking.startTime,
      overlapEnd: conflictBooking.endTime,
      impactSummary,
      canMove: true,
      actionTaken: 'none',
    };
  }

  return {
    isFullyBooked,
    availablePeriods,
    conflictDetails,
    explanation,
  };
}

// ---------------------------------------------------------------------------
// Soft signal: Utilisation
// met = under-utilised (below target). Exposes utilisationHeadroom.
// ---------------------------------------------------------------------------
function computeUtilisationSignal(
  resource: Resource,
  startDate: Date,
  endDate: Date,
  existingBookings: Booking[]
): MatchSignal & { utilisationHeadroom: number } {
  const bookingDays = calendarDaysBetween(startDate, endDate);
  const halfWindow = Math.min(60, Math.max(7, Math.round(bookingDays / 2)));

  const windowStart = new Date(startDate);
  windowStart.setDate(windowStart.getDate() - halfWindow);
  const windowEnd = new Date(endDate);
  windowEnd.setDate(windowEnd.getDate() + halfWindow);

  const windowDays = Math.round((dateOnlyMs(windowEnd) - dateOnlyMs(windowStart)) / 86400000) + 1;
  const bookedDays = new Set<number>();
  const windowStartMs = dateOnlyMs(windowStart);

  for (const booking of existingBookings) {
    if (!isBookingForResource(booking, resource.id)) continue;
    const bStart = new Date(booking.startTime);
    const bEnd = new Date(booking.endTime);
    if (bEnd < windowStart || bStart > windowEnd) continue;

    let cur = dateOnlyMs(new Date(Math.max(bStart.getTime(), windowStart.getTime())));
    const capEnd = dateOnlyMs(new Date(Math.min(bEnd.getTime(), windowEnd.getTime())));
    while (cur <= capEnd) {
      bookedDays.add(Math.round((cur - windowStartMs) / 86400000));
      cur += 86400000;
    }
  }

  const utilisationPct = Math.round((bookedDays.size / windowDays) * 100);
  const TARGET_UTILISATION = 80;
  const met = utilisationPct < TARGET_UTILISATION;
  const utilisationHeadroom = Math.max(0, TARGET_UTILISATION - utilisationPct);

  return {
    key: 'utilisation',
    label: 'Utilisation',
    met,
    explanation: met
      ? `${utilisationPct}% utilised — ${utilisationHeadroom}% headroom available`
      : `${utilisationPct}% utilised — at or above target capacity`,
    utilisationHeadroom,
  };
}

// ---------------------------------------------------------------------------
// Soft signal: Role
// met = exact match or role-family/synonym match.
// ---------------------------------------------------------------------------
function computeRoleSignal(resource: Resource, requiredRole: string | undefined): MatchSignal {
  if (!requiredRole || requiredRole.trim() === '') {
    return {
      key: 'role',
      label: 'Role',
      met: true,
      explanation: 'No role requirement specified',
    };
  }

  const req = requiredRole.trim().toLowerCase();
  const res = (resource.role || '').trim().toLowerCase();

  // Exact match
  if (res === req) {
    return {
      key: 'role',
      label: 'Role',
      met: true,
      explanation: `Exact role match: ${resource.role}`,
    };
  }

  // Family / synonym lookup
  const reqFamily = ALIAS_TO_FAMILY.get(req);
  const resFamily = ALIAS_TO_FAMILY.get(res);

  if (reqFamily && resFamily && reqFamily === resFamily) {
    return {
      key: 'role',
      label: 'Role',
      met: true,
      explanation: `Equivalent role — same discipline as ${requiredRole}`,
    };
  }

  return {
    key: 'role',
    label: 'Role',
    met: false,
    explanation: `Role mismatch — requires ${requiredRole}, resource is ${resource.role || 'unspecified'}`,
  };
}

// ---------------------------------------------------------------------------
// Soft signal: Skills
// met = all required skills covered (auto-pass when none required).
// Exposes skillsCoveragePct (100 when none required).
// ---------------------------------------------------------------------------
function computeSkillsSignal(resource: Resource, requiredSkills: string[]): MatchSignal & { skillsCoveragePct: number } {
  if (!requiredSkills || requiredSkills.length === 0) {
    return {
      key: 'skills',
      label: 'Skills',
      met: true,
      explanation: 'No skills requirement specified',
      skillsCoveragePct: 100,
    };
  }

  const resourceSkills = resource.skills || [];

  if (resourceSkills.length === 0) {
    return {
      key: 'skills',
      label: 'Skills',
      met: false,
      explanation: `No skills recorded — 0 of ${requiredSkills.length} required skills matched`,
      skillsCoveragePct: 0,
    };
  }

  const resourceSkillNames = new Set(resourceSkills.map(s => s.name.toLowerCase()));
  let matchedCount = 0;
  const matchedNames: string[] = [];
  const missingNames: string[] = [];

  for (const required of requiredSkills) {
    if (resourceSkillNames.has(required.toLowerCase())) {
      matchedCount++;
      matchedNames.push(required);
    } else {
      missingNames.push(required);
    }
  }

  const skillsCoveragePct = Math.round((matchedCount / requiredSkills.length) * 100);
  const met = matchedCount === requiredSkills.length;

  let explanation: string;
  if (matchedCount === 0) {
    explanation = `None of the ${requiredSkills.length} required skills matched`;
  } else if (met) {
    explanation = `All ${matchedCount} required skills matched`;
  } else {
    const missing = missingNames.length <= 2
      ? missingNames.join(', ')
      : `${missingNames.slice(0, 2).join(', ')} +${missingNames.length - 2} more`;
    explanation = `${matchedCount} of ${requiredSkills.length} skills matched — missing: ${missing}`;
  }

  return {
    key: 'skills',
    label: 'Skills',
    met,
    explanation,
    skillsCoveragePct,
  };
}

// ---------------------------------------------------------------------------
// Main engine
// ---------------------------------------------------------------------------
export function computeRecommendations(
  input: RecommendationEngineInput
): RecommendationEngineOutput {
  const {
    startDate,
    endDate,
    requiredRole,
    requiredSkills,
    allResources,
    existingBookings,
  } = input;

  if (!startDate || !endDate) {
    return { cleanMatches: [], conflictMatches: [], computedAt: Date.now() };
  }

  const cleanMatches: ResourceMatchResult[] = [];
  const conflictMatches: ResourceMatchResult[] = [];

  const scorableResources = allResources.filter(r => r.id !== 'unallocated');

  for (const resource of scorableResources) {
    const availabilityResult = computeAvailabilitySignal(resource, startDate, endDate, existingBookings);
    const utilisationResult  = computeUtilisationSignal(resource, startDate, endDate, existingBookings);
    const roleSignal         = computeRoleSignal(resource, requiredRole);
    const skillsResult       = computeSkillsSignal(resource, requiredSkills || []);

    const signals: MatchSignal[] = [
      { key: utilisationResult.key, label: utilisationResult.label, met: utilisationResult.met, explanation: utilisationResult.explanation },
      roleSignal,
      { key: skillsResult.key, label: skillsResult.label, met: skillsResult.met, explanation: skillsResult.explanation },
    ];

    const softSignalsMet = signals.filter(s => s.met).length;
    const skillsCoveragePct = skillsResult.skillsCoveragePct;
    const utilisationHeadroom = utilisationResult.utilisationHeadroom;

    const availabilityStatus: ResourceMatchResult['availabilityStatus'] =
      availabilityResult.isFullyBooked
        ? 'conflict'
        : availabilityResult.availablePeriods.some(p => p.type === 'partial')
        ? 'partial'
        : 'free';

    const result: ResourceMatchResult = {
      resource,
      softSignalsMet,
      skillsCoveragePct,
      utilisationHeadroom,
      signals,
      availabilityStatus,
      availablePeriods: availabilityResult.availablePeriods,
      conflictDetails: availabilityResult.conflictDetails,
    };

    if (availabilityResult.isFullyBooked) {
      conflictMatches.push(result);
    } else {
      cleanMatches.push(result);
    }
  }

  // Sort: softSignalsMet desc, skillsCoveragePct desc, utilisationHeadroom desc
  const sortFn = (a: ResourceMatchResult, b: ResourceMatchResult) => {
    if (b.softSignalsMet !== a.softSignalsMet) return b.softSignalsMet - a.softSignalsMet;
    if (b.skillsCoveragePct !== a.skillsCoveragePct) return b.skillsCoveragePct - a.skillsCoveragePct;
    return b.utilisationHeadroom - a.utilisationHeadroom;
  };

  cleanMatches.sort(sortFn);
  conflictMatches.sort(sortFn);

  return {
    cleanMatches,
    conflictMatches,
    computedAt: Date.now(),
  };
}
